import * as React from "react"
import { X, ChevronDown, Loader2 } from "lucide-react"
import { cn } from "../../lib/utils"
import { Badge } from "./badge"

interface TagInputProps {
  value?: string[]
  onChange?: (value: string[]) => void
  options?: { value: string; label: string }[]
  placeholder?: string
  loading?: boolean
  allowClear?: boolean
  showSearch?: boolean
  onSearch?: (value: string) => void
  className?: string
  disabled?: boolean
}

const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  ({
    value = [],
    onChange,
    options = [],
    placeholder = "Select or create tags...",
    loading = false,
    allowClear = true,
    showSearch = true,
    onSearch,
    className,
    disabled = false,
  }, ref) => {
    const [inputValue, setInputValue] = React.useState("")
    const [isOpen, setIsOpen] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)

    // Filter options based on input and exclude already selected
    const filteredOptions = React.useMemo(() => {
      const lowerInput = inputValue.toLowerCase()
      return options.filter(option =>
        !value.includes(option.value) &&
        (showSearch ? option.label.toLowerCase().includes(lowerInput) : true)
      )
    }, [options, value, inputValue, showSearch])

    // Check if input matches any existing option
    const inputMatchesOption = React.useMemo(() => {
      return options.some(opt => opt.value.toLowerCase() === inputValue.toLowerCase())
    }, [options, inputValue])

    // Check if input is already selected
    const inputAlreadySelected = React.useMemo(() => {
      return value.some(v => v.toLowerCase() === inputValue.toLowerCase())
    }, [value, inputValue])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      onSearch?.(newValue)
      if (!isOpen) setIsOpen(true)
    }

    const handleSelect = (selectedValue: string) => {
      if (!value.includes(selectedValue)) {
        onChange?.([...value, selectedValue])
      }
      setInputValue("")
      inputRef.current?.focus()
    }

    const handleRemove = (removedValue: string) => {
      onChange?.(value.filter(v => v !== removedValue))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && inputValue.trim()) {
        e.preventDefault()
        // Create new tag if it doesn't exist or select existing
        const trimmedValue = inputValue.trim()
        if (!inputAlreadySelected) {
          handleSelect(trimmedValue)
        }
      } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
        // Remove last tag on backspace when input is empty
        handleRemove(value[value.length - 1])
      } else if (e.key === "Escape") {
        setIsOpen(false)
        inputRef.current?.blur()
      }
    }

    const handleClearAll = (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange?.([])
      setInputValue("")
    }

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    return (
      <div ref={containerRef} className={cn("relative", className)}>
        <div
          ref={ref}
          className={cn(
            "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm transition-colors",
            "focus-within:outline-none focus-within:ring-1 focus-within:ring-ring",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          onClick={() => {
            if (!disabled) {
              inputRef.current?.focus()
              setIsOpen(true)
            }
          }}
        >
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1 pr-1"
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(tag)
                  }}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </Badge>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={value.length === 0 ? placeholder : ""}
            disabled={disabled}
            className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-[80px]"
          />
          <div className="flex items-center gap-1">
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {allowClear && value.length > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClearAll}
                className="rounded-full p-0.5 hover:bg-muted"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (filteredOptions.length > 0 || (inputValue && !inputAlreadySelected)) && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            {/* Option to create new tag */}
            {inputValue && !inputMatchesOption && !inputAlreadySelected && (
              <div
                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleSelect(inputValue.trim())}
              >
                Create "{inputValue.trim()}"
              </div>
            )}
            {/* Existing options */}
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)

TagInput.displayName = "TagInput"

export { TagInput }
